import { useEffect, useRef, useState } from 'react';
import { Activity, Heart, Monitor, ClipboardCheck, Play, Users, BarChart3, Stethoscope, CheckCircle } from 'lucide-react';

const DURATION = 30;

const SUBTITLES = [
  { from: 0, to: 3, text: 'Simulación clínica inteligente' },
  { from: 3, to: 8, text: 'Prepara escenarios clínicos' },
  { from: 8, to: 15, text: 'Simula decisiones clínicas en un entorno seguro' },
  { from: 15, to: 21, text: 'Supervisa e interviene en tiempo real' },
  { from: 21, to: 26, text: 'Evalúa cada decisión' },
  { from: 26, to: 30, text: 'Entrena decisiones clínicas antes de enfrentarlas en la vida real' },
];

export const Vital = ({ label, value, color }) => (
  <div className="bg-slate-900/80 rounded-lg p-2 text-center border border-slate-700">
    <div className={`text-lg md:text-2xl font-bold font-mono ${color}`}>{value}</div>
    <div className="text-[9px] md:text-[10px] text-slate-400 uppercase tracking-wide">{label}</div>
  </div>
);

export const SceneLogo = ({ final }) => (
  <div className="flex flex-col items-center justify-center h-full gap-4 animate-[fadeIn_0.8s_ease]">
    <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-2xl shadow-cyan-500/40">
      <Activity className="w-9 h-9 md:w-11 md:h-11 text-white" />
    </div>
    <div className="text-2xl md:text-4xl font-bold text-white">Simu<span className="text-cyan-400">Clinic</span></div>
    {final && (
      <div className="text-center">
        <p className="text-cyan-300 text-sm md:text-base font-medium mt-1">Solicita una demostración</p>
        <p className="text-slate-400 text-xs md:text-sm">www.simuclinic.cl</p>
      </div>
    )}
  </div>
);

export const ScenePrepare = () => (
  <div className="h-full p-4 md:p-8 flex flex-col justify-center">
    <div className="text-cyan-400 text-xs font-bold uppercase tracking-widest mb-3">Prepárate.</div>
    <div className="grid grid-cols-3 gap-2 md:gap-3">
      {[
        { icon: Heart, n: 'Paro Cardiorrespiratorio', e: 'Emergencias', c: 'text-rose-400' },
        { icon: Stethoscope, n: 'Crisis Asmática Severa', e: 'Emergencias', c: 'text-cyan-400' },
        { icon: Monitor, n: 'IAM con SDST', e: 'Cardiología', c: 'text-amber-400' },
      ].map((s, i) => (
        <div key={i} className="bg-slate-800/90 border border-slate-700 rounded-xl p-2.5 md:p-4"
          style={{ animation: `slideUp 0.5s ease ${i * 0.25}s both` }}>
          <s.icon className={`w-5 h-5 md:w-6 md:h-6 ${s.c} mb-2`} />
          <div className="text-white text-[10px] md:text-xs font-semibold leading-tight">{s.n}</div>
          <div className="text-slate-500 text-[9px] md:text-[10px] mt-1">{s.e}</div>
          <div className="mt-2 h-1 rounded bg-slate-700 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500" style={{ width: `${60 + i * 15}%` }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const SceneSimulate = ({ t }) => {
  const fc = Math.round(118 + Math.sin(t * 4) * 4);
  return (
    <div className="h-full p-3 md:p-6 flex flex-col justify-center gap-2 md:gap-3">
      <div className="text-cyan-400 text-xs font-bold uppercase tracking-widest">Simula. Decide.</div>
      <div className="grid grid-cols-4 gap-1.5 md:gap-2">
        <Vital label="FC" value={fc} color="text-emerald-400" />
        <Vital label="PA" value="135/85" color="text-cyan-400" />
        <Vital label="SpO2" value="86%" color="text-rose-400" />
        <Vital label="FR" value="30" color="text-amber-400" />
      </div>
      <div className="flex gap-1.5 md:gap-2 flex-wrap">
        {['Oxigenoterapia', 'Salbutamol 5 mg NEB', 'Hidrocortisona IV', 'Solicitar exámenes'].map((a, i) => (
          <span key={a} className={`text-[9px] md:text-[11px] px-2 py-1 rounded-full border ${i === 1 ? 'bg-cyan-500/20 border-cyan-400 text-cyan-300' : 'bg-slate-800 border-slate-700 text-slate-300'}`}
            style={{ animation: `slideUp 0.4s ease ${0.3 + i * 0.3}s both` }}>
            {i === 1 && <CheckCircle className="inline w-3 h-3 mr-1 -mt-0.5" />}{a}
          </span>
        ))}
      </div>
      <div className="bg-slate-900/80 border border-slate-700 rounded-lg p-2 text-[9px] md:text-[11px] text-emerald-300"
        style={{ animation: 'slideUp 0.4s ease 1.6s both' }}>
        ▲ El paciente responde al tratamiento: SpO2 mejora a 93%
      </div>
    </div>
  );
};

export const SceneSupervise = () => (
  <div className="h-full p-3 md:p-6 flex flex-col justify-center gap-2 md:gap-3">
    <div className="text-purple-400 text-xs font-bold uppercase tracking-widest">Supervisa.</div>
    <div className="flex items-center gap-2 bg-slate-800/90 border border-slate-700 rounded-lg p-2">
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
      </span>
      <Users className="w-4 h-4 text-cyan-400" />
      <span className="text-white text-[10px] md:text-xs font-medium">María Torres — Crisis Asmática · EN VIVO</span>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div className="bg-slate-900/80 border border-slate-700 rounded-lg p-2 space-y-1">
        {['02:14 Oxigenoterapia iniciada', '03:40 Salbutamol 5 mg NEB', '05:02 Control SpO2: 93%'].map((e, i) => (
          <div key={e} className="text-[9px] md:text-[10px] text-slate-300 flex gap-1" style={{ animation: `slideUp 0.4s ease ${0.4 + i * 0.4}s both` }}>
            <span className="text-cyan-500 font-mono">{e.slice(0, 5)}</span>{e.slice(6)}
          </div>
        ))}
      </div>
      <div className="bg-slate-900/80 border border-purple-500/40 rounded-lg p-2 flex flex-col gap-1.5 justify-center">
        {['Enviar pista', 'Modificar vitales', 'Pausar simulación'].map((b, i) => (
          <span key={b} className="text-[9px] md:text-[10px] text-purple-200 bg-purple-500/20 border border-purple-500/40 rounded px-2 py-1 text-center"
            style={{ animation: `slideUp 0.4s ease ${0.6 + i * 0.35}s both` }}>{b}</span>
        ))}
      </div>
    </div>
  </div>
);

export const SceneEvaluate = () => (
  <div className="h-full p-3 md:p-6 flex flex-col justify-center gap-2 md:gap-3">
    <div className="text-emerald-400 text-xs font-bold uppercase tracking-widest">Evalúa. Mejora.</div>
    <div className="flex items-center gap-3">
      <div className="text-3xl md:text-5xl font-bold text-white">87<span className="text-lg text-slate-400">/100</span></div>
      <BarChart3 className="w-8 h-8 text-cyan-400" />
    </div>
    <div className="space-y-1.5">
      {[
        { l: 'Manejo clínico', v: 92, c: 'from-emerald-500 to-emerald-400' },
        { l: 'Tiempos de respuesta', v: 84, c: 'from-cyan-500 to-cyan-400' },
        { l: 'Comunicación', v: 78, c: 'from-purple-500 to-purple-400' },
      ].map((b, i) => (
        <div key={b.l}>
          <div className="flex justify-between text-[9px] md:text-[11px] text-slate-300 mb-0.5"><span>{b.l}</span><span>{b.v}%</span></div>
          <div className="h-1.5 md:h-2 rounded bg-slate-700 overflow-hidden">
            <div className={`h-full rounded bg-gradient-to-r ${b.c}`} style={{ width: `${b.v}%`, animation: `growBar 1s ease ${0.3 + i * 0.3}s both` }} />
          </div>
        </div>
      ))}
    </div>
    <div className="text-[9px] md:text-[10px] text-slate-400">Debriefing automático con retroalimentación por decisión</div>
  </div>
);

const SceneCycle = ({ t }) => (
  <div className="h-full flex flex-col items-center justify-center gap-4">
    {t < 28 ? (
      <div className="flex items-center gap-2 md:gap-4">
        {['Preparar', 'Simular', 'Evaluar'].map((s, i) => (
          <div key={s} className="flex items-center gap-2 md:gap-4">
            <div className="px-3 py-2 md:px-5 md:py-3 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/50 text-white text-xs md:text-base font-bold"
              style={{ animation: `slideUp 0.4s ease ${i * 0.3}s both` }}>{s}</div>
            {i < 2 && <span className="text-cyan-400 text-lg">→</span>}
          </div>
        ))}
      </div>
    ) : (
      <SceneLogo final />
    )}
  </div>
);

export default function AnimatedDemo({ playing, loop, onTime, onEnded, resetKey }) {
  const [t, setT] = useState(0);
  const raf = useRef(null);
  const last = useRef(null);
  const timeRef = useRef(0);

  useEffect(() => { setT(0); timeRef.current = 0; last.current = null; }, [resetKey]);

  useEffect(() => {
    if (!playing) { cancelAnimationFrame(raf.current); last.current = null; return; }
    const tick = (ts) => {
      if (last.current == null) last.current = ts;
      const dt = (ts - last.current) / 1000;
      last.current = ts;
      let next = timeRef.current + dt;
      let finished = false;
      if (next >= DURATION) {
        if (loop) { next = 0; } else { next = DURATION; finished = true; }
      }
      timeRef.current = next;
      setT(next);
      onTime?.(next, DURATION);
      if (finished) { onEnded?.(); return; }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [playing, loop, onEnded, onTime]);

  const subtitle = SUBTITLES.find(s => t >= s.from && t < s.to)?.text || SUBTITLES[SUBTITLES.length - 1].text;
  const scene = t < 3 ? <SceneLogo /> : t < 8 ? <ScenePrepare /> : t < 15 ? <SceneSimulate t={t} />
    : t < 21 ? <SceneSupervise /> : t < 26 ? <SceneEvaluate /> : <SceneCycle t={t} />;

  return (
    <div className="relative w-full h-full bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 overflow-hidden" data-testid="animated-demo">
      <style>{`
        @keyframes slideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes growBar { from { width: 0; } }
      `}</style>
      <div key={Math.floor(t < 3 ? 0 : t < 8 ? 1 : t < 15 ? 2 : t < 21 ? 3 : t < 26 ? 4 : 5)} className="absolute inset-0 pb-14" style={{ animation: 'fadeIn 0.5s ease' }}>
        {scene}
      </div>
      {/* Subtítulos incrustados */}
      <div className="absolute bottom-8 left-0 right-0 px-4 text-center pointer-events-none">
        <span className="inline-block bg-black/60 text-white text-xs md:text-sm font-medium px-3 py-1.5 rounded-lg" data-testid="demo-subtitle">
          {subtitle}
        </span>
      </div>
      {/* Barra de progreso */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-800">
        <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-[width] duration-100" style={{ width: `${(t / DURATION) * 100}%` }} />
      </div>
    </div>
  );
}
